import BemIt, { addClass } from "@gtechdoodler/bem-it";
import { useEffect, useState } from "react";
import { NumberFormatValues } from "react-number-format";
import useViewport from "../../../../hooks/useViewport";
import Icon from "../../../imagery/Icon";

import {
	StyledBidButton,
	StyledInput,
	StyledInputWrapper,
} from "./ProxyBidNumberField.styles";
import { ProxyBidNumberFieldProps } from "./ProxyBidNumberField.types";

const ProxyBidNumberField = ({
	value,
	min,
	max,
	step,
	onChange,
	error = false,
	prefix,
	thousandSeparator,
	suspended,
	controlsDisabled,
}: ProxyBidNumberFieldProps) => {
	const block = new BemIt("ProxyBidNumberField");

	const [animationClassName, setAnimationClassName] = useState<"bounce" | "">(
		""
	);
	const viewport = useViewport();
	const isMobileDevice = ["xs", "sm"].includes(viewport?.currentSize);

	const increment = () => {
		setAnimationClassName("bounce");
		let newValue = value !== undefined ? value - (value % step) + step : min;

		const valueToUpdate = max && newValue > max ? max : newValue;

		onChange(valueToUpdate);
	};

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;
		if (animationClassName) {
			timeoutId = setTimeout(() => {
				setAnimationClassName("");
			}, 200);
		}
		return () => clearTimeout(timeoutId);
	}, [animationClassName]);

	const decrement = () => {
		setAnimationClassName("bounce");

		let newValue;

		if (value) {
			newValue = value % step !== 0 ? value - (value % step) : value - step;
		} else {
			newValue = min;
		}

		const valueToUpdate = newValue < min ? min : newValue;

		onChange(valueToUpdate);
	};

	const onValueChange = (newValues: NumberFormatValues) => {
		const newValue = newValues.floatValue;
		if (newValue) {
			onChange(newValue);
		}
	};

	const isAllowed = (values: NumberFormatValues) => {
		const { floatValue } = values;

		return floatValue && max ? floatValue <= max : true;
	};

	return (
		<StyledInputWrapper
			className={addClass(animationClassName).after(block)}
			error={error}
			htmlTag="div"
		>
			<StyledBidButton
				top={0}
				left={0}
				disabled={value === min || suspended || controlsDisabled}
				onClick={decrement}
				aria-label="decrement"
			>
				<Icon name="minus" size="sm" color="accent" marginLeft="md" />
			</StyledBidButton>
			<StyledInput
				onValueChange={onValueChange}
				type="text"
				inputMode="numeric"
				value={value}
				prefix={prefix}
				min={min}
				max={max}
				step={step}
				thousandSeparator={thousandSeparator}
				decimalScale={0}
				isAllowed={isAllowed}
				isMobileDevice={isMobileDevice}
			/>
			<StyledBidButton
				disabled={
					(typeof max !== "undefined" && value === max) ||
					suspended ||
					controlsDisabled
				}
				top={0}
				right={0}
				onClick={increment}
				aria-label="increment"
			>
				<Icon name="plus" size="sm" color="accent" marginRight="md" />
			</StyledBidButton>
		</StyledInputWrapper>
	);
};

export default ProxyBidNumberField;
